import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { setDoc } from 'firebase/firestore';
import {
  COL,
  DEFAULT_LOYALTY_CONFIG,
  LOYALTY_CONFIG_ID,
  LOYALTY_PROGRAM_ID,
  loyaltyConfigWriteSchema,
  loyaltyLedgerPath,
  toPublicProgram,
  type LoyaltyAccount,
  type LoyaltyConfig,
  type LoyaltyLedgerEntry,
} from '@eprintsmart/shared';
import { db } from '../firebase';

/**
 * The loyalty programme config, including the per-tier earn rate. The config
 * doc is admin-only (never world-readable), which is what keeps the rate off
 * the storefront. Functions read the same doc with the admin SDK.
 */

const configRef = () => doc(db, COL.loyaltyConfig, LOYALTY_CONFIG_ID);

export function watchLoyaltyConfig(cb: (config: LoyaltyConfig) => void): () => void {
  return onSnapshot(configRef(), (snap) => {
    // A missing doc means "never configured" — hand back the shipped default so
    // the form always has sensible values to edit and save.
    cb(snap.exists() ? (snap.data() as LoyaltyConfig) : DEFAULT_LOYALTY_CONFIG);
  });
}

export async function saveLoyaltyConfig(input: unknown): Promise<void> {
  const parsed = loyaltyConfigWriteSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? 'The loyalty settings are not valid.');
  }
  const config = { ...parsed.data, updatedAt: Date.now() };
  await setDoc(configRef(), config, { merge: true });
  // Publish the rate-free projection the storefront reads. Written in the same
  // action so the two can never drift — and it never carries the earn rate.
  await setDoc(doc(db, COL.loyaltyProgram, LOYALTY_PROGRAM_ID), {
    ...toPublicProgram(config),
    updatedAt: config.updatedAt,
  });
}

/** Look up one customer's wallet (balance + tier) by auth uid. */
export async function getLoyaltyAccount(uid: string): Promise<LoyaltyAccount | null> {
  const snap = await getDoc(doc(db, COL.loyaltyAccounts, uid));
  return snap.exists() ? (snap.data() as LoyaltyAccount) : null;
}

/** That customer's most recent ledger entries, newest first. */
export async function getLoyaltyLedger(uid: string, max = 50): Promise<LoyaltyLedgerEntry[]> {
  const snap = await getDocs(
    query(collection(db, loyaltyLedgerPath(uid)), orderBy('createdAt', 'desc')),
  );
  return snap.docs.slice(0, max).map((d) => ({ ...(d.data() as LoyaltyLedgerEntry), id: d.id }));
}

/** A wallet plus whatever the CRM knows about who owns it. */
export interface LoyaltyAccountRow extends LoyaltyAccount {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Every customer wallet, richest first.
 *
 * Firestore rules allow this: `allow read` on loyaltyAccounts/{uid} covers list
 * as well as get, and isAdmin() holds regardless of the uid wildcard, so the
 * query is satisfiable for every document it could return.
 *
 * Wallets are keyed by auth uid, which is also the CRM contact id for a
 * signed-in customer — so the contact lookup turns a column of uids into
 * recognisable people. Guests never earn credits (awardCredits skips non-member
 * uids), so there is no guest_ case to resolve here.
 */
export async function listLoyaltyAccounts(max = 200): Promise<LoyaltyAccountRow[]> {
  const snap = await getDocs(
    query(collection(db, COL.loyaltyAccounts), orderBy('balanceMinor', 'desc'), limit(max)),
  );
  // Doc id after the spread: the stored uid field should match the key, but the
  // key is the one Firestore actually indexed by, so it wins.
  const rows = snap.docs.map((d) => ({ ...(d.data() as LoyaltyAccount), uid: d.id }));

  // Resolve names in parallel; a missing contact is not an error, it just means
  // the wallet shows its uid.
  const contacts = await Promise.all(
    rows.map(async (r) => {
      try {
        const c = await getDoc(doc(db, COL.crmContacts, r.uid));
        return c.exists() ? (c.data() as { name?: string; email?: string; phone?: string }) : null;
      } catch {
        return null;
      }
    }),
  );

  return rows.map((r, i) => ({ ...r, ...(contacts[i] ?? {}) }));
}
